import { useQueryClient } from '@tanstack/react-query'
import { apiBaseUrl, apiClient } from '../api/client'
import { $api } from '../api/queryClient'
import type { BallotReceipt, BallotSession, BallotState, LiveState } from '../api/schema'

/**
 * General assembly convocation and remote vote (spec 2026-10-05-08).
 *
 * Two audiences, two auth models, like the signatures:
 *   - the managing agent, under /api/v1/organizations/{org}/contracts/{uid},
 *     authenticated like everything else (`contracts.*`);
 *   - the voter, under /api/v1/public/ballots/{orgCode}, where the session
 *     obtained by exchanging the link token is the whole authorization. No
 *     Authorization header is ever sent there.
 */

const BASE = '/api/v1/organizations/{orgCode}/contracts/{contractUid}'

export function useAssemblyPreparation(org: string, contractUid: string) {
    return $api.useQuery('get', `${BASE}/preparation` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/preparation', {
        params: { path: { orgCode: org, contractUid } },
    })
}

export function useAssemblyBallots(org: string, contractUid: string, enabled: boolean) {
    return $api.useQuery('get', `${BASE}/ballots` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/ballots', {
        params: { path: { orgCode: org, contractUid } },
    }, { enabled })
}

export function useContractEvents(org: string, contractUid: string, enabled: boolean) {
    return $api.useQuery('get', `${BASE}/events` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/events', {
        params: { path: { orgCode: org, contractUid } },
    }, { enabled })
}

/** Every write refreshes every read of the contract (preparation, ballots, journal, children). */
export function useConvocationMutations() {
    const queryClient = useQueryClient()
    const onSuccess = () =>
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]
                return key[0] === 'get' && typeof key[1] === 'string' && key[1].includes('/contracts')
            },
        })
    return {
        prepare: $api.useMutation('post', `${BASE}/prepare` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/prepare', { onSuccess }),
        convene: $api.useMutation('post', `${BASE}/convene` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/convene', { onSuccess }),
        remind: $api.useMutation('post', `${BASE}/ballots/remind` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/ballots/remind', { onSuccess }),
        reissue: $api.useMutation('post', `${BASE}/ballots/{stakeholderUid}/reissue` as '/api/v1/organizations/{orgCode}/contracts/{contractUid}/ballots/{stakeholderUid}/reissue', { onSuccess }),
    }
}

/** Downloads the postal convocation of one ballot (a fresh link with its QR code). */
export async function downloadLetter(org: string, contractUid: string, stakeholderUid: string) {
    const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/contracts/{contractUid}/ballots/{stakeholderUid}/letter', {
        params: { path: { orgCode: org, contractUid, stakeholderUid } },
        parseAs: 'blob',
    })
    if (error || !data) throw new Error('letter')
    const url = URL.createObjectURL(data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `convocation-${stakeholderUid.slice(0, 8)}.pdf`
    a.click()
    URL.revokeObjectURL(url)
}

// ---- Voter side ------------------------------------------------------------

export class BallotApiError extends Error {
    status: number
    code?: string
    constructor(status: number, message: string, code?: string) {
        super(message)
        this.status = status
        this.code = code
    }
}

async function ballotFetch<T>(orgCode: string, path: string, session: string | null, init: { method?: string; json?: unknown } = {}): Promise<T> {
    const headers: Record<string, string> = {}
    if (session) headers['X-Ballot-Session'] = session
    if (init.json !== undefined) headers['Content-Type'] = 'application/json'
    const resp = await fetch(`${apiBaseUrl}/api/v1/public/ballots/${encodeURIComponent(orgCode)}/${path}`, {
        method: init.method ?? 'GET',
        headers,
        body: init.json !== undefined ? JSON.stringify(init.json) : undefined,
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
    })
    if (!resp.ok) {
        let message = resp.statusText
        let code: string | undefined
        try {
            const body = await resp.json()
            message = body?.fields?.[0]?.message || body?.title || body?.message || message
            code = body?.code
        } catch {
            // not JSON
        }
        throw new BallotApiError(resp.status, message, code)
    }
    const type = resp.headers.get('Content-Type') || ''
    if (type.includes('application/pdf')) return (await resp.arrayBuffer()) as T
    return (await resp.json()) as T
}

export interface ProxyResult {
    warnings: string[]
    state: BallotState
}

export const ballotApi = {
    openSession: (orgCode: string, token: string) => ballotFetch<BallotSession>(orgCode, 'session', null, { method: 'POST', json: { token } }),
    current: (orgCode: string, session: string) => ballotFetch<BallotState>(orgCode, 'current', session),
    challenge: (orgCode: string, session: string) => ballotFetch<BallotSession>(orgCode, 'challenge', session, { method: 'POST', json: {} }),
    verify: (orgCode: string, session: string, code: string) =>
        ballotFetch<BallotSession>(orgCode, 'verify', session, { method: 'POST', json: { code } }),
    beginPasskey: (orgCode: string, session: string) =>
        ballotFetch<{ options: Record<string, any>; session: string }>(orgCode, 'passkey', session, { method: 'POST', json: {} }),
    verifyPasskey: (orgCode: string, session: string, passkeySession: string, credential: Record<string, unknown>) =>
        ballotFetch<BallotSession>(orgCode, 'verify', session, {
            method: 'POST',
            json: { passkey_session: passkeySession, passkey_credential: credential },
        }),
    submitForm: (orgCode: string, session: string, answers: Record<string, string>, place: string) =>
        ballotFetch<BallotReceipt>(orgCode, 'form', session, { method: 'PUT', json: { answers, place, consent: true } }),
    formPdf: (orgCode: string, session: string) => ballotFetch<ArrayBuffer>(orgCode, 'form.pdf', session),
    proxy: (orgCode: string, session: string, body: { holder_stakeholder_uid?: string; holder_name?: string }) =>
        ballotFetch<ProxyResult>(orgCode, 'proxy', session, { method: 'POST', json: { ...body, consent: true } }),
    mode: (orgCode: string, session: string, mode: string) => ballotFetch<BallotState>(orgCode, 'mode', session, { method: 'PUT', json: { mode } }),
    // The meeting (spec 2026-10-05-09).
    live: (orgCode: string, session: string) => ballotFetch<LiveState>(orgCode, 'live', session),
    liveVote: (orgCode: string, session: string, body: { resolution_uid: string; vote: string; for_stakeholder_uid?: string }) =>
        ballotFetch<{ unchanged: boolean; state: LiveState }>(orgCode, 'live/vote', session, { method: 'PUT', json: body }),
    checkIn: (orgCode: string, session: string) => ballotFetch<LiveState>(orgCode, 'check-in', session, { method: 'POST', json: {} }),
    question: (orgCode: string, session: string, body: { kind: 'question' | 'hand'; body?: string }) =>
        ballotFetch<LiveState>(orgCode, 'questions', session, { method: 'POST', json: body }),
}

/**
 * Reads the server-sent events of the participant's meeting with fetch (the
 * session rides in a header, which EventSource cannot send) and calls
 * onChange on every event. Returns the function that stops it.
 */
export function followLiveStream(orgCode: string, session: string, onChange: () => void): () => void {
    const controller = new AbortController()
    void (async () => {
        try {
            const resp = await fetch(`${apiBaseUrl}/api/v1/public/ballots/${encodeURIComponent(orgCode)}/live/stream`, {
                headers: { 'X-Ballot-Session': session },
                signal: controller.signal,
            })
            const reader = resp.body?.getReader()
            if (!reader) return
            const decoder = new TextDecoder()
            for (;;) {
                const { done, value } = await reader.read()
                if (done) return
                if (decoder.decode(value).includes('event: change')) onChange()
            }
        } catch {
            // aborted or cut: the page keeps polling
        }
    })()
    return () => controller.abort()
}
